/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { ReactElement, ReactNode, useEffect, useState } from "react"
import { Button, FlexContainer, useBreakpointComponent } from "@carrotfertility/carotene"
import { Autocomplete, Box, TextField } from "@carrotfertility/carotene-core"
import { ICalendlyLink, ITalkToCarrotLanguage } from "../../contentful-types"
import TwoColumnRightPrimary from "../views/atoms/layouts/TwoColumnRightPrimary"
import { Spacer } from "../views/atoms/Atoms"
import {
  StaticAssetID,
  getTalkToCarrotLanguageGuidance,
  getTalkToCarrotLanguages
} from "../../lib/contentful/contentfulClient"
import { GuidanceElement } from "../../lib/contentful/components/GuidanceElement"
import {
  fieldIsValid,
  getNextActionType,
  getNextLanguageAction,
  getPathForLanguage,
  getTTCLanguageOption,
  TileNextActionType
} from "../../lib/contentful/utils/mappingUtils"
import { NeedHelpLink, SmallHeaderOverride } from "./util/talkToCarrotMolecules"
import { LanguageOption } from "../dynamic-content/util/dynamicContent"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"
import { getEmployeeId, getPreferredLocale } from "../../reducers/userInfo"
import { TalkToCarrotMultilingualImage } from "./TalkToCarrotMultilingualImage"
import { TalkToCarrotSelectOption } from "./TalkToCarrotSelectOption"
import { useIntlSort } from "utils/hooks/useIntlSort"
import { FormattedMessage, useIntl } from "react-intl"
import { toCondition } from "../../lib/contentful/utils/conditionUtils"
import { useDoesUserSatisfyConditions } from "services/contentful/conditional-gate"
import { useGetPhoneSupportNumberForUser } from "components/dynamic-content/hooks/useQueryPhoneSupport"
import { PopupModal } from "react-calendly"

const setEnglishToTop = ({ entryId }): 0 | -1 => {
  if (entryId === StaticAssetID.TALK_TO_CARROT_BENEFIT_CHAT_LANGUAGE_ENGLISH) {
    return -1
  }
  return 0
}

type TalkToCarrotLanguagePageProps = {
  baseUrl: string
  helpNode?: ReactNode
}

interface LocalizedLanguageOption extends LanguageOption {
  formattedLabel: string
}

export function TalkToCarrotLanguagePage({ helpNode, baseUrl }: TalkToCarrotLanguagePageProps): JSX.Element {
  const [talkToCarrotLanguages, setTalkToCarrotLanguages] = useState<ITalkToCarrotLanguage[]>(null)
  const [talkToCarrotLanguageOptions, setTalkToCarrotLanguageOptions] = useState<LocalizedLanguageOption[]>(null)
  const [selectedLanguageOption, setSelectedLanguageOption] = useState<LanguageOption>(null)
  const [guidance, setGuidance] = useState<ReactElement<typeof GuidanceElement>>(null)
  const history = useHistory()
  const employeeId = useSelector(getEmployeeId)
  const preferredLocale = useSelector(getPreferredLocale)
  const { localeComparator } = useIntlSort()
  const { doesUserSatisfyConditions, isLoading } = useDoesUserSatisfyConditions()
  const { data: phoneSupportNumber } = useGetPhoneSupportNumberForUser()
  const [open, setOpen] = useState(false)
  const [selectedLanguageUrl, setSelectedLanguageUrl] = useState(null)

  const formatLanguages = (option: LanguageOption): LocalizedLanguageOption => {
    return {
      ...option,
      formattedLabel: intl.formatDisplayName(option.value, { type: "language" })
    }
  }

  async function loadTalkToCarrotLanguages(): Promise<void> {
    const languages = await getTalkToCarrotLanguages()
    let languageOptions: LocalizedLanguageOption[]
    if (!isLoading) {
      languageOptions = languages
        ?.filter(({ fields: { conditions } }) => !conditions || doesUserSatisfyConditions(conditions.map(toCondition)))
        .map(getTTCLanguageOption)
        .map(formatLanguages)
        .sort(({ formattedLabel: labelA }, { formattedLabel: labelB }) => localeComparator(labelA, labelB))
        .sort(setEnglishToTop)
    }
    setTalkToCarrotLanguages(languages)
    setTalkToCarrotLanguageOptions(languageOptions)
  }

  useEffect(() => {
    if (!talkToCarrotLanguageOptions) {
      loadTalkToCarrotLanguages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function loadGuidance(): Promise<void> {
      const guidance = await getTalkToCarrotLanguageGuidance(preferredLocale)
      setGuidance(<GuidanceElement guidance={guidance} />)
    }

    if (!guidance) {
      loadGuidance()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const GuidanceWithHelp = ({ guidance }: { guidance: ReactElement<typeof GuidanceElement> }): JSX.Element => (
    <>
      {guidance}
      {helpNode && helpNode}
    </>
  )

  function setSelectedOptionToTop(selectedOption: LocalizedLanguageOption): void {
    setTalkToCarrotLanguageOptions(
      selectedOption
        ? [
            selectedOption,
            ...talkToCarrotLanguageOptions
              .filter(({ label }) => label !== selectedOption.label)
              .sort(({ formattedLabel: labelA }, { formattedLabel: labelB }) => localeComparator(labelA, labelB))
              .sort(setEnglishToTop)
          ]
        : talkToCarrotLanguageOptions
    )
  }

  function onLanguagePickerChange(_event, selectedOption): void {
    setSelectedLanguageOption(selectedOption)
    setSelectedOptionToTop(selectedOption)
  }
  const CalendlyWidget = ({ url }): JSX.Element => {
    useEffect(() => {
      const handleEsc = (event): void => {
        if (event.keyCode === 27) {
          setOpen(false)
        }
      }
      window.addEventListener("keydown", handleEsc)

      return () => {
        window.removeEventListener("keydown", handleEsc)
      }
    }, [])

    return (
      <PopupModal
        url={url}
        utm={{ utmCampaign: employeeId }}
        onModalClose={() => setOpen(false)}
        open={open}
        rootElement={document.getElementById("continue-button-box")}
      />
    )
  }

  function findLanguageFromOption(): ITalkToCarrotLanguage {
    return talkToCarrotLanguages?.find((language) => language?.fields?.name === selectedLanguageOption?.label)
  }

  function onSubmit() {
    const language = findLanguageFromOption()
    const nextAction = getNextLanguageAction(language)

    if (fieldIsValid(nextAction)) {
      switch (getNextActionType(nextAction)) {
        case TileNextActionType.TILE_PAGE:
          return history.push(getPathForLanguage({ language, baseUrl }))
        case TileNextActionType.CALENDLY_LINK:
          setOpen(true)
          setSelectedLanguageUrl((nextAction as ICalendlyLink)?.fields?.url)
          return

        case TileNextActionType.PHONE_SUPPORT_NUMBER:
          return window.open(`tel:${phoneSupportNumber?.e164FormatPhoneNumber}`, "_self")
      }
    } else {
      return history.push("/404")
    }
  }

  const continueButton = (
    <Box id="continue-button-box">
      <Button onClick={onSubmit} disabled={!selectedLanguageOption} id="continueButton">
        <FormattedMessage defaultMessage="Continue" />
      </Button>
      {selectedLanguageUrl && <CalendlyWidget url={selectedLanguageUrl} />}
    </Box>
  )

  const continueButtonWithHelp = (
    <FlexContainer justifyContent="space-between" alignItems="center">
      <NeedHelpLink baseUrl={baseUrl} />
      {continueButton}
    </FlexContainer>
  )

  const intl = useIntl()
  const textfieldPlaceholder = intl.formatMessage({
    defaultMessage: "Select language"
  })

  const languagePicker = talkToCarrotLanguageOptions ? (
    <Autocomplete
      aria-label={intl.formatMessage({ defaultMessage: "Preferred language" })}
      disablePortal
      multiple={false}
      onChange={onLanguagePickerChange}
      value={selectedLanguageOption}
      getOptionLabel={(option) => option.formattedLabel}
      options={talkToCarrotLanguageOptions}
      clearOnEscape
      renderOption={(props, state) => {
        return (
          <TalkToCarrotSelectOption
            {...props}
            key={state.value}
            isSelected={state.value === selectedLanguageOption?.value}
          >
            {state.formattedLabel}
          </TalkToCarrotSelectOption>
        )
      }}
      renderInput={(params) => (
        <TextField type="select" name="language" helperText=" " placeholder={textfieldPlaceholder} {...params} />
      )}
    />
  ) : (
    <></>
  )

  const largeContent = (
    <TwoColumnRightPrimary
      secondaryContent={<GuidanceWithHelp guidance={guidance} />}
      primaryContent={
        <>
          <TalkToCarrotMultilingualImage />
          {languagePicker}
          <FlexContainer justifyContent="flex-end">{continueButton}</FlexContainer>
        </>
      }
    />
  )

  const mediumContent = (
    <>
      {guidance}
      <Spacer height={3} />
      <TalkToCarrotMultilingualImage />
      {languagePicker}
      {continueButtonWithHelp}
    </>
  )

  const smallContent = (
    <SmallHeaderOverride>
      {guidance}
      <Spacer height={2} />
      {languagePicker}
      {continueButtonWithHelp}
    </SmallHeaderOverride>
  )

  return useBreakpointComponent([smallContent, smallContent, mediumContent, largeContent]) || <></>
}
